// Presentational dashboard cards extracted from Dashboard.js to keep the page
// component small and focused on state/data-fetching. Pure props in, JSX out.
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  ConfidenceBadge, DisclaimerNote, EmptyState, ScoreGauge, SliderField, TrendChip,
} from "@/components/shared";
import { HelpTip } from "@/components/help";
import { CalendarCheck, FileText, Flame, Gauge, HandHeart, Heart, Lightbulb, RefreshCw, Sparkles, User, Users } from "lucide-react";

export const StreakChip = ({ streak }) => {
  if (!streak || streak.current < 1) return null;
  return (
    <div data-testid="streak-chip" className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--warning)/0.4)] bg-[hsl(var(--warning)/0.1)] px-3 py-1.5 text-xs font-semibold text-[hsl(var(--warning))]">
      <Flame className="w-3.5 h-3.5" />
      <span data-testid="streak-chip-days">{streak.current}-day streak</span>
      {streak.best > streak.current && <span className="font-normal text-muted-foreground">· best {streak.best}</span>}
    </div>
  );
};

export const QuickCheckinCard = ({ pet, ci, setCi, busy, onSubmit, streak }) => (
  <Card className="md:col-span-5 rounded-2xl" data-testid="dashboard-checkin-card">
    <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><CalendarCheck className="w-4 h-4 text-primary" /> Quick Check-In
      <HelpTip title="Quick Check-In" text="A 30-second daily pulse: your mood, your pet's mood, stress, and connection. Saving it refreshes your Bond Score, keeps your streak alive, and generates today's personalized ritual + enrichment ideas." testId="help-checkin" />
    </CardTitle></CardHeader>
    <CardContent className="space-y-3">
      <Input placeholder="How do you feel today?" value={ci.humanMood} onChange={(e) => setCi({ ...ci, humanMood: e.target.value })} data-testid="dashboard-checkin-human-mood-input" />
      <Input placeholder={`How does ${pet?.name || "your pet"} seem?`} value={ci.petMood} onChange={(e) => setCi({ ...ci, petMood: e.target.value })} data-testid="dashboard-checkin-pet-mood-input" />
      <SliderField label="Your stress" value={ci.stressLevel} onChange={(v) => setCi({ ...ci, stressLevel: v })} testId="dashboard-checkin-stress-slider" />
      <SliderField label="Connection quality" value={ci.connectionQuality} onChange={(v) => setCi({ ...ci, connectionQuality: v })} testId="dashboard-checkin-connection-slider" />
      <Button className="w-full rounded-xl" onClick={onSubmit} disabled={busy} data-testid="dashboard-checkin-submit-button">{busy ? "Saving..." : "Save check-in"}</Button>
      {streak && (
        <p className="text-[11px] text-muted-foreground text-center" data-testid="checkin-streak-hint">
          {streak.todayDone
            ? (streak.nextMilestone ? `Streak: ${streak.current} day${streak.current === 1 ? "" : "s"} — next milestone at ${streak.nextMilestone}.` : `Streak: ${streak.current} days. Incredible.`)
            : streak.current > 0 ? `Check in today to keep your ${streak.current}-day streak alive.` : "Check in daily to start a streak."}
        </p>
      )}
    </CardContent>
  </Card>
);

export const BondScoreCard = ({ pet, score, calcBusy, onCalc }) => (
  <Card className="md:col-span-7 rounded-2xl" data-testid="dashboard-bond-score-card">
    <CardHeader className="pb-2 flex-row items-center justify-between">
      <CardTitle className="text-sm font-semibold flex items-center gap-2"><Gauge className="w-4 h-4 text-primary" /> Bond Score {pet ? `· ${pet.name}` : ""}
        <HelpTip title="Bond Score" text="A 0-100 mirror of your relationship's health, computed from check-ins, wellness logs, and completed rituals. The trend shows direction; confidence rises with consistent logging. Never a judgment - just a signal." testId="help-bond-score" />
      </CardTitle>
      <Link to="/bond-score" className="text-xs text-primary hover:underline">History →</Link>
    </CardHeader>
    <CardContent>
      {score ? (
        <div className="flex flex-col sm:flex-row items-center gap-6">
          <ScoreGauge score={score.score} />
          <div className="flex-1 space-y-2">
            <div className="flex gap-2 flex-wrap"><TrendChip trend={score.trend} /><ConfidenceBadge level={score.confidence} /></div>
            <p className="text-sm text-muted-foreground">{score.explanation}</p>
            <Button size="sm" variant="secondary" className="rounded-lg" onClick={onCalc} disabled={calcBusy} data-testid="dashboard-recalculate-score-button">
              {calcBusy ? "Calculating..." : "Recalculate"}
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center py-4 gap-3">
          <p className="text-sm text-muted-foreground">No Bond Score yet — want to generate the first one?</p>
          <Button onClick={onCalc} disabled={calcBusy} className="rounded-xl" data-testid="dashboard-calculate-score-button">{calcBusy ? "Calculating..." : "Calculate Bond Score"}</Button>
        </div>
      )}
      <div className="mt-3"><DisclaimerNote /></div>
    </CardContent>
  </Card>
);

export const EnrichmentCard = ({ pet, enrichment, onRegenerate, regenBusy }) => (
  <Card className="md:col-span-7 rounded-2xl" data-testid="dashboard-enrichment-card">
    <CardHeader className="pb-2 flex-row items-center justify-between">
      <CardTitle className="text-sm font-semibold flex items-center gap-2"><Lightbulb className="w-4 h-4 text-primary" /> Today's Enrichment Ideas {pet ? `· ${pet.name}` : ""}
        <HelpTip title="Enrichment Ideas" text="3-5 fresh, breed- and state-aware activity ideas generated after each daily check-in. They never repeat what you've seen recently. Not feeling today's batch? Regenerate for a brand-new set (uses one AI call)." testId="help-enrichment" />
      </CardTitle>
      {enrichment?.ideas?.length > 0 && onRegenerate && (
        <Button size="sm" variant="ghost" className="rounded-lg text-muted-foreground hover:text-primary h-7 px-2" onClick={onRegenerate} disabled={regenBusy} data-testid="regenerate-plan-button-enrichment">
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${regenBusy ? "animate-spin" : ""}`} /> {regenBusy ? "Crafting..." : "Regenerate"}
        </Button>
      )}
    </CardHeader>
    <CardContent>
      {enrichment?.ideas?.length ? (
        <div className="space-y-2.5">
          {enrichment.ideas.map((idea, i) => (
            <div key={`${i}:${idea.slice(0, 40)}`} className="flex items-start gap-2.5 text-sm" data-testid={`enrichment-idea-${i}`}>
              <span className="w-5 h-5 rounded-full bg-secondary text-primary text-[10px] font-semibold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
              <p className="text-muted-foreground leading-relaxed">{idea}</p>
            </div>
          ))}
          <p className="text-[11px] text-muted-foreground pt-1">Tailored to {pet?.breed ? `${pet.name} (${pet.breed})` : pet?.name || "your pet"} from today's check-in and recent history.</p>
        </div>
      ) : (
        <EmptyState icon={Lightbulb} title="No ideas yet today" hint="Save today's check-in and fresh, breed-aware enrichment ideas plus a daily ritual will appear here automatically." />
      )}
    </CardContent>
  </Card>
);

export const RitualCard = ({ ritual, onRegenerate, regenBusy }) => (
  <Card className="md:col-span-5 rounded-2xl" data-testid="dashboard-ritual-card">
    <CardHeader className="pb-2 flex-row items-center justify-between">
      <CardTitle className="text-sm font-semibold flex items-center gap-2"><HandHeart className="w-4 h-4 text-primary" /> Today's Ritual
        <HelpTip title="Today's Ritual" text="A short, science-informed bonding activity crafted for your pet after each check-in - tuned to their breed, age, and today's mood, and never a repeat of recent rituals. Complete it to feed your Bond Score, or regenerate if it doesn't fit today." testId="help-ritual" />
      </CardTitle>
      <Link to="/rituals" className="text-xs text-primary hover:underline">All rituals →</Link>
    </CardHeader>
    <CardContent>
      {ritual ? (
        <div className="space-y-2">
          <p className="font-medium text-sm">{ritual.title}</p>
          <p className="text-sm text-muted-foreground line-clamp-3">{ritual.description}</p>
          <p className="text-xs text-muted-foreground">{ritual.durationMinutes} min · {ritual.category}{ritual.aiGenerated ? " · AI-generated" : ""}</p>
          <div className="flex items-center gap-2 mt-1">
            <Link to="/rituals"><Button size="sm" className="rounded-lg" data-testid="dashboard-complete-ritual-button">Complete this ritual</Button></Link>
            {onRegenerate && (
              <Button size="sm" variant="ghost" className="rounded-lg text-muted-foreground hover:text-primary" onClick={onRegenerate} disabled={regenBusy} data-testid="regenerate-plan-button">
                <RefreshCw className={`w-3.5 h-3.5 mr-1 ${regenBusy ? "animate-spin" : ""}`} /> {regenBusy ? "Crafting..." : "Regenerate"}
              </Button>
            )}
          </div>
        </div>
      ) : (
        <EmptyState icon={HandHeart} title="No active rituals" hint="Generate a gentle, AI-tailored ritual for your pet." action={<Link to="/rituals"><Button size="sm" variant="secondary" className="rounded-lg">Create ritual</Button></Link>} />
      )}
    </CardContent>
  </Card>
);

export const WellnessSnapshotCard = ({ latestWellness }) => (
  <Card className="md:col-span-4 rounded-2xl" data-testid="dashboard-wellness-snapshot-card">
    <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><Heart className="w-4 h-4 text-primary" /> Pet Wellness Snapshot</CardTitle></CardHeader>
    <CardContent>
      {latestWellness ? (
        <div className="space-y-1.5 text-sm">
          {[["Appetite", latestWellness.appetite], ["Energy", latestWellness.energy], ["Sleep", latestWellness.sleep], ["Anxiety", latestWellness.anxiety]].map(([k, v]) => (
            <div key={k} className="flex items-center justify-between">
              <span className="text-muted-foreground">{k}</span>
              <div className="flex items-center gap-2">
                <div className="w-24 h-1.5 rounded-full bg-muted overflow-hidden"><div className="h-full rounded-full bg-primary" style={{ width: `${v * 10}%` }} /></div>
                <span className="text-xs font-semibold w-8 text-right">{v}/10</span>
              </div>
            </div>
          ))}
          <p className="text-xs text-muted-foreground pt-1">Bathroom: {latestWellness.bathroom} · {latestWellness.date}</p>
        </div>
      ) : <EmptyState icon={Heart} title="No wellness logs yet" hint="Log appetite, energy, sleep and anxiety to feed the Bond Score." action={<Link to="/wellness"><Button size="sm" variant="secondary" className="rounded-lg">Add log</Button></Link>} />}
    </CardContent>
  </Card>
);

export const MoodSnapshotCard = ({ pet, latestCheckin }) => (
  <Card className="md:col-span-4 rounded-2xl" data-testid="dashboard-mood-snapshot-card">
    <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><User className="w-4 h-4 text-primary" /> Human Mood Snapshot</CardTitle></CardHeader>
    <CardContent>
      {latestCheckin ? (
        <div className="space-y-2 text-sm">
          <p><span className="text-muted-foreground">You felt:</span> <span className="font-medium">{latestCheckin.humanMood}</span></p>
          <p><span className="text-muted-foreground">{pet?.name || "Pet"} seemed:</span> <span className="font-medium">{latestCheckin.petMood}</span></p>
          <div className="flex items-center justify-between"><span className="text-muted-foreground">Stress</span><span className="text-xs font-semibold">{latestCheckin.stressLevel}/10</span></div>
          <div className="flex items-center justify-between"><span className="text-muted-foreground">Connection</span><span className="text-xs font-semibold">{latestCheckin.connectionQuality}/10</span></div>
          <p className="text-xs text-muted-foreground">{latestCheckin.date}</p>
        </div>
      ) : <EmptyState icon={User} title="No check-ins yet" hint="A 30-second check-in captures both sides of the bond." />}
    </CardContent>
  </Card>
);

export const ReportsReadyCard = ({ reports }) => (
  <Card className="md:col-span-4 rounded-2xl" data-testid="dashboard-reports-card">
    <CardHeader className="pb-2 flex-row items-center justify-between">
      <CardTitle className="text-sm font-semibold flex items-center gap-2"><FileText className="w-4 h-4 text-primary" /> Reports Ready</CardTitle>
      <Link to="/reports" className="text-xs text-primary hover:underline">All →</Link>
    </CardHeader>
    <CardContent>
      {reports.length ? (
        <div className="space-y-2">
          {reports.map((r) => (
            <Link key={r.id} to="/reports" className="block text-sm hover:text-primary transition-colors">
              <p className="font-medium capitalize">{r.type.replace(/_/g, " ")}</p>
              <p className="text-xs text-muted-foreground">{new Date(r.createdAt).toLocaleDateString()}</p>
            </Link>
          ))}
        </div>
      ) : <p className="text-sm text-muted-foreground py-2">No reports yet. Generate a vet-ready report before your next visit.</p>}
    </CardContent>
  </Card>
);

export const CoachPromptCard = ({ pet, coachPrompt, setCoachPrompt, onAsk }) => (
  <Card className="md:col-span-7 rounded-2xl" data-testid="dashboard-coach-card">
    <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><Sparkles className="w-4 h-4 text-primary" /> Ask the AI Bond Coach</CardTitle></CardHeader>
    <CardContent className="space-y-3">
      <Textarea rows={2} placeholder={`e.g. "How can I help ${pet?.name || "my pet"} feel calmer during storms?"`} value={coachPrompt} onChange={(e) => setCoachPrompt(e.target.value)} data-testid="dashboard-coach-input" />
      <div className="flex items-center justify-between gap-3">
        <p className="text-[11px] text-muted-foreground">Non-diagnostic guidance with safety labels. Emergencies go to your vet.</p>
        <Button className="rounded-xl shrink-0" disabled={!coachPrompt.trim()} onClick={onAsk} data-testid="dashboard-coach-ask-button">Ask coach</Button>
      </div>
    </CardContent>
  </Card>
);

export const CareCircleCard = ({ careCircle }) => (
  <Card className="md:col-span-5 rounded-2xl" data-testid="dashboard-care-circle-card">
    <CardHeader className="pb-2 flex-row items-center justify-between">
      <CardTitle className="text-sm font-semibold flex items-center gap-2"><Users className="w-4 h-4 text-primary" /> Care Circle</CardTitle>
      <Link to="/care-circle" className="text-xs text-primary hover:underline">Manage →</Link>
    </CardHeader>
    <CardContent>
      {careCircle.length ? (
        <div className="space-y-2">
          {careCircle.map((m) => (
            <div key={m.id} className="flex items-center justify-between text-sm">
              <div><p className="font-medium">{m.name}</p><p className="text-xs text-muted-foreground">{m.role}</p></div>
              <span className="text-xs px-2 py-0.5 rounded-full bg-secondary capitalize">{m.status}</span>
            </div>
          ))}
        </div>
      ) : <EmptyState icon={Users} title="No care circle yet" hint="Invite a trusted caregiver or provider with scoped permissions." />}
    </CardContent>
  </Card>
);

export const RecentLogsCard = ({ wellness }) => (
  <Card className="md:col-span-12 rounded-2xl" data-testid="dashboard-recent-logs-card">
    <CardHeader className="pb-2 flex-row items-center justify-between">
      <CardTitle className="text-sm font-semibold flex items-center gap-2"><Heart className="w-4 h-4 text-primary" /> Recent Wellness Logs</CardTitle>
      <Link to="/wellness" className="text-xs text-primary hover:underline">All logs →</Link>
    </CardHeader>
    <CardContent>
      {wellness.length ? (
        <div className="space-y-2">
          {wellness.map((w) => (
            <div key={w.id} className="flex items-center justify-between text-sm border-b border-border last:border-0 pb-2 last:pb-0">
              <span className="text-muted-foreground">{w.date}</span>
              <span className="text-xs">appetite {w.appetite} · energy {w.energy} · sleep {w.sleep} · anxiety {w.anxiety}</span>
            </div>
          ))}
        </div>
      ) : <p className="text-sm text-muted-foreground py-2">No logs yet — want to start with a 30-second wellness log?</p>}
    </CardContent>
  </Card>
);
